import { createTheme } from "@mui/material/styles";

export function createAppTheme(primaryColor = "#2951A3") {
  return createTheme({
    palette: {
      primary: {
        main: primaryColor,
        contrastText: "#fff",
      },
      secondary: {
        main: "#3CC6A0", // Vibrant teal for action
        contrastText: "#fff",
      },
      background: {
        default: "#F8FAFC", // Clean, light background
        paper: "#fff",
      },
      success: {
        main: "#21BA45",
      },
      warning: {
        main: "#FFD684", // Friendly gold accent
      },
      text: {
        primary: "#203060",
        secondary: "#5A6478",
      },
    },
    shape: {
      borderRadius: 14,
    },
    typography: {
      fontFamily:
        '"Inter", "Inter Variable", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
      fontWeightBold: 800,
      fontWeightRegular: 400,
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
      button: { textTransform: "none", fontWeight: 700 },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 999,
            fontWeight: 800,
            fontSize: "1.07rem",
            letterSpacing: 0.3,
            textTransform: "none",
            boxShadow: "none",
          },
          containedPrimary: {
            // Darkened from #3CC6A0/#6be2c1 (contrast ~2.1:1) to pass WCAG AA 4.5:1
            background: "linear-gradient(92deg,#0a6e55,#0d7859 70%)",
            color: "#fff",
            boxShadow: "0 2px 16px rgba(10,110,85,0.4)",
            "&:hover": {
              background: "linear-gradient(92deg,#085e48,#0b6a4f 70%)",
              boxShadow: "0 2px 24px rgba(10,110,85,0.45)",
            },
            "&:disabled": {
              background: "linear-gradient(92deg,#b0e3d5,#eaf7f2 70%)",
              color: "#e0e0e0",
              boxShadow: "none",
            },
          },
          outlined: {
            color: "#3CC6A0",
            borderColor: "#3CC6A0",
            background: "#fff",
            "&:hover": {
              borderColor: "#2bb086",
              background: "#f4fcf7",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 24,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 32,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // Fallback first: some iPad Safari versions mishandle/ignore dvh.
            // Keep dialogs within viewport so content/actions remain reachable.
            maxHeight: "calc(100vh - 32px)",
            "@supports (height: 100dvh)": {
              maxHeight: "calc(100dvh - 32px)",
            },
            "@media (max-width:600px)": {
              width: "calc(100% - 12px)",
              maxWidth: "calc(100% - 12px)",
              margin: 6,
              maxHeight:
                "calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 12px)",
              "@supports (height: 100dvh)": {
                maxHeight:
                  "calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 12px)",
              },
              borderRadius: 24,
            },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            rowGap: 8,
            "@media (max-width:600px)": {
              paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
              flexWrap: "wrap",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            borderRadius: 999,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            // iOS Safari zooms the whole page when a focused input renders
            // below 16px. That zoom never resets and is a major reason the
            // app "feels broken" on iPhone. 16px is the floor, phones only.
            "@media (max-width:900px)": {
              fontSize: 16,
            },
          },
          input: {
            "@media (max-width:900px)": {
              fontSize: 16,
            },
          },
        },
      },
      // Native selects and multiline fields zoom for the same reason.
      MuiInputBase: {
        styleOverrides: {
          input: {
            "@media (max-width:900px)": {
              fontSize: 16,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // WCAG 2.5.5 / Apple HIG minimum tap target on touch devices.
            // Desktop keeps its denser sizing via the pointer query.
            "@media (hover: none) and (pointer: coarse)": {
              minWidth: 44,
              minHeight: 44,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            "@media (hover: none) and (pointer: coarse)": {
              minHeight: 48,
            },
          },
        },
      },
    },
  });
}

const theme = createAppTheme();
export default theme;
